import { useState } from "react";
import { FilterBase } from "@components/commerce/product-listing/filters/common/filter/FilterBase";
import { Text } from "@ecco/ui/text";

import {
    styles,
    localCheckboxStyle,
} from "@components/commerce/product-listing/filters/toolbar/item/price/PriceToolbarFilterStyles";
import { HIERARCHICAL_CATEGORIES_SEPARATOR } from "@lib/constants/filtersConstants";
import type { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import { useMoney } from "@ecco/ui/use-money";
import { activeSiteCurrency } from "@config/site/site-config";
import { Box } from "@ecco/ui/box";
import { scrollbarStyles } from "@ecco/ui/css/scrollbar";
import { SelectDropdownItem } from "@ecco/ui/select-dropdown";
import { Checkbox } from "@ecco/ui/checkbox";
import { focusOutline } from "@ecco/ui/css/focus";
import { useLocale } from "next-intl";

export interface PriceToolbarFilterProps {
    initialFilters: Partial<RefinementListItem>[];
    filterName: string;
    items: RefinementListItem[];
    refine: (value: string) => void;
    isToolbar: boolean;
    isLoading?: boolean;
}

export const PriceToolbarFilter = ({
    initialFilters,
    items,
    refine,
    filterName,
    isToolbar,
    isLoading,
}: PriceToolbarFilterProps) => {
    const dropdownItems = (
        <Box
            sx={{
                ...scrollbarStyles,
                height: "auto",
                maxHeight: "500px",
                overflowY: "auto",
                mx: 2,
                padding: 2,
                bgColor: "white",
            }}
        >
            {initialFilters?.map((item) => {
                const categoryHierarchy = item.label.split(HIERARCHICAL_CATEGORIES_SEPARATOR);
                const dynamicItem = items.find((it) => it.value === item.value);
                return (
                    <SelectDropdownItem
                        key={item.label}
                        sx={localCheckboxStyle}
                        onClick={() => {
                            refine(item.value);
                        }}
                        isDisabled={!dynamicItem}
                    >
                        <CheckboxWithHoveredState
                            dynamicItem={dynamicItem}
                            item={item}
                            categoryHierarchy={categoryHierarchy}
                            categoryRefine={refine}
                        />
                    </SelectDropdownItem>
                );
            })}
        </Box>
    );

    return (
        <FilterBase
            isLoading={isLoading}
            dropdownItems={dropdownItems}
            filterName={filterName}
            isToolbar={isToolbar}
        />
    );
};

const CheckboxWithHoveredState = ({ categoryHierarchy, dynamicItem, item, categoryRefine }) => {
    const [hovered, setHovered] = useState(false);
    const locale = useLocale();
    const money = useMoney({
        money: {
            centAmount: 0,
            currencyCode: activeSiteCurrency,
            fractionDigits: 0,
        },
        locale,
        convertFromCents: true,
    });

    const priceLabel =
        money.currencyNarrowSymbol + " " + categoryHierarchy[categoryHierarchy.length - 1];

    return (
        <Box
            sx={styles.itemWrapper}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {dynamicItem && (
                <Checkbox
                    _focus={{
                        ...focusOutline,
                    }}
                    aria-label={priceLabel}
                    colorScheme={dynamicItem?.isRefined || !hovered ? "black" : "gray"}
                    key={item.label}
                    value={item.label}
                    isChecked={hovered || dynamicItem?.isRefined}
                    isDisabled={!dynamicItem}
                    onChange={() => {
                        categoryRefine(item.value);
                    }}
                />
            )}
            <Text noOfLines={1} fontWeight={"bold"}>
                {priceLabel}
            </Text>
            {dynamicItem && (
                <Text color={"gray.700"} fontWeight={"bold"}>
                    ({dynamicItem?.count})
                </Text>
            )}
        </Box>
    );
};

export default PriceToolbarFilter;
